import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Sparkles, 
  Users, 
  PartyPopper,
  Music,
  UtensilsCrossed,
  Lightbulb,
  Car,
  Check,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';

const GARDEN_HERO = "https://images.unsplash.com/photo-1762216444919-043cf813e4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxvdXRkb29yJTIwd2VkZGluZyUyMGdhcmRlbnxlbnwwfHx8fDE3NzUwNzkxODF8MA&ixlib=rb-4.1.0&q=85";
const WEDDING_IMAGE = "https://images.pexels.com/photos/14703685/pexels-photo-14703685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const eventTypes = [
  { 
    icon: Heart, 
    title: 'Bodas', 
    desc: 'Celebra el día más importante de tu vida en un entorno natural y romántico. Nuestro jardín es el escenario perfecto para tu ceremonia y recepción.',
    color: 'bg-pink-50 text-pink-600'
  },
  { 
    icon: Sparkles, 
    title: 'XV Años', 
    desc: 'Una celebración mágica para una ocasión especial. Amplios espacios para que la quinceañera y sus invitados bailen hasta el amanecer.',
    color: 'bg-purple-50 text-purple-600'
  },
  { 
    icon: Users, 
    title: 'Eventos Corporativos', 
    desc: 'Retiros empresariales, team buildings y conferencias en un ambiente relajado que inspira creatividad y conexión.',
    color: 'bg-blue-50 text-blue-600'
  },
  { 
    icon: PartyPopper, 
    title: 'Fiestas Privadas', 
    desc: 'Cumpleaños, bautizos, graduaciones y cualquier celebración especial. Tu evento, tu estilo.',
    color: 'bg-amber-50 text-amber-600'
  },
];

const features = [
  { icon: Users, label: 'Hasta 200 invitados' },
  { icon: Music, label: 'Zona DJ/Banda' },
  { icon: UtensilsCrossed, label: 'Área de Catering' },
  { icon: Lightbulb, label: 'Iluminación Ambiental' },
  { icon: Car, label: '50+ Estacionamientos' },
  { icon: Check, label: 'Baños para Invitados' },
];

const packages = [
  {
    name: 'Básico',
    features: [
      'Uso del jardín por 8 horas',
      'Mesas y sillas (hasta 150 personas)',
      'Iluminación básica',
      'Estacionamiento',
      'Baños',
    ],
    cta: 'Ideal para eventos íntimos'
  },
  {
    name: 'Premium',
    highlight: true,
    features: [
      'Todo el paquete Básico',
      'Uso por 12 horas',
      'Iluminación decorativa completa',
      'Zona de coctel',
      'Área de fotos',
      'Coordinador de evento',
    ],
    cta: 'Nuestra opción más popular'
  },
  {
    name: 'All Inclusive',
    features: [
      'Todo el paquete Premium',
      'Uso de la Villa (hospedaje)',
      'Montaje y desmontaje',
      'Coordinación con proveedores',
      'Decoración floral básica',
      'Servicio de meseros (6)',
    ],
    cta: 'La experiencia completa'
  },
];

export default function GardenPage() {
  return (
    <div data-testid="garden-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${GARDEN_HERO})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        </div>

        <div className="relative h-full flex flex-col justify-end pb-16 px-4">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Celebra con Nosotros
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
                Jardín de Eventos
              </h1>
              <p className="text-white/80 text-lg max-w-2xl">
                1,500 m² de área verde con vista a las montañas. El lugar perfecto 
                para crear recuerdos que durarán toda la vida.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#3D5A3E] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '200', label: 'Invitados' },
              { value: '1,500', label: 'm² de jardín' },
              { value: '50+', label: 'Estacionamientos' },
              { value: '100%', label: 'Naturaleza' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-serif text-3xl md:text-4xl text-white">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Para toda ocasión
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Tipos de Eventos
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {eventTypes.map((event, index) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className={`w-14 h-14 rounded-xl ${event.color} flex items-center justify-center mb-4`}>
                  <event.icon className="h-7 w-7" />
                </div>
                <h3 className="font-serif text-2xl text-[#1A1A1A] mb-3">{event.title}</h3>
                <p className="text-[#1A1A1A]/70 leading-relaxed">{event.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                Todo para Tu Evento
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-8">
                Nuestro jardín cuenta con todas las instalaciones necesarias para 
                que tu evento sea un éxito. Desde iluminación ambiental hasta 
                amplio estacionamiento, hemos pensado en cada detalle.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature.label}
                    className="flex items-center space-x-3 p-3 bg-[#FAF7F2] rounded-lg"
                  >
                    <feature.icon className="h-5 w-5 text-[#C8962D]" />
                    <span className="text-sm text-[#1A1A1A]">{feature.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={WEDDING_IMAGE}
                  alt="Wedding Setup"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#5C4033] text-white p-6 rounded-xl">
                <p className="font-serif text-2xl">Vista</p>
                <p className="text-white/80">a las montañas</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Opciones flexibles
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Paquetes de Eventos
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`rounded-2xl p-8 ${
                  pkg.highlight
                    ? 'bg-[#5C4033] text-white ring-4 ring-[#C8962D] scale-105'
                    : 'bg-white'
                }`}
              >
                {pkg.highlight && (
                  <span className="inline-block bg-[#C8962D] text-white text-xs px-3 py-1 rounded-full mb-4">
                    Más Popular
                  </span>
                )}
                <h3 className={`font-serif text-2xl mb-6 ${pkg.highlight ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {pkg.name}
                </h3>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className={`h-5 w-5 mr-3 flex-shrink-0 ${pkg.highlight ? 'text-[#C8962D]' : 'text-[#3D5A3E]'}`} />
                      <span className={pkg.highlight ? 'text-white/90' : 'text-[#1A1A1A]/70'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className={`text-sm mb-6 ${pkg.highlight ? 'text-white/70' : 'text-[#1A1A1A]/60'}`}>
                  {pkg.cta}
                </p>
                <Link to="/contacto">
                  <Button
                    className={`w-full ${
                      pkg.highlight
                        ? 'bg-[#C8962D] hover:bg-[#B8861D] text-white'
                        : 'bg-[#5C4033] hover:bg-[#3D5A3E] text-white'
                    }`}
                  >
                    Solicitar Cotización
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-[#1A1A1A]/60 mt-8">
            * Los precios varían según fecha y requerimientos específicos. Contáctanos para una cotización personalizada.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#3D5A3E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            ¿Tienes un evento en mente?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Platícanos sobre tu celebración y te ayudaremos a hacerla realidad
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button
                data-testid="garden-contact-btn"
                size="lg"
                className="bg-[#C8962D] hover:bg-[#B8861D] text-white px-12"
              >
                Enviar Consulta
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:+523111234567">
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white/10 px-12"
              >
                <Phone className="mr-2 h-5 w-5" />
                Llamar Ahora
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
